import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LeadManagement from './LeadManagement';
import EmployeeManagement from './EmployeeManagementNew'; // Changed to use new component with service specialization
import WorkflowOverview from './WorkflowOverview';
import LinkGenerator from './LinkGenerator';
import ChatSystem from './ChatSystem';
import DropdownOptionsManager from './DropdownOptionsManager';
import TourPackageManagement from './TourPackageManagement';
import MasterDataManagement from './MasterDataManagement';
import ServiceManagement from './ServiceManagement';
import CompanySettings from './CompanySettings';
import FollowUpNotificationBell from './FollowUpNotificationBell';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('leads');
  const [showChat, setShowChat] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    // Listen for navigate to follow-ups event
    const handleNavigateToFollowUps = () => {
      setActiveTab('leads');
      // Dispatch event to LeadManagement to switch to followups tab
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('switchToFollowUpsTab'));
      }, 100);
    };

    window.addEventListener('navigateToFollowUps', handleNavigateToFollowUps);
    return () => window.removeEventListener('navigateToFollowUps', handleNavigateToFollowUps);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleChatToggle = () => {
    setShowChat(prev => !prev);
  };

  const menuItems = [
    { id: 'leads', icon: '📊', label: 'Lead Management', component: <LeadManagement /> },
    { id: 'employees', icon: '👥', label: 'Employee Management', component: <EmployeeManagement /> },
    { id: 'service-management', icon: '🔧', label: 'Service Management', component: <ServiceManagement /> },
    { id: 'workflow', icon: '🔄', label: 'Workflow Overview', component: <WorkflowOverview /> },
    { id: 'links', icon: '🔗', label: 'Link Generator', component: <LinkGenerator /> },
    { id: 'dropdown-options', icon: '⚙️', label: 'Dropdown Options', component: <DropdownOptionsManager /> },
    { id: 'packages', icon: '🎒', label: 'Tour Packages', component: <TourPackageManagement /> },
    { id: 'master-data', icon: '📚', label: 'Master Data', component: <MasterDataManagement /> },
    { id: 'company-settings', icon: '⚙️', label: 'Company Settings', component: <CompanySettings /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-20' : 'w-64'} bg-gradient-to-b from-blue-900 to-blue-800 text-white transition-all duration-300 ease-in-out flex flex-col shadow-xl fixed h-screen z-20`}>
        {/* Logo & Brand */}
        <div className="p-6 border-b border-blue-700">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <div>
                <h2 className="text-xl font-bold">Gajanan Tours & Travels</h2>
                <p className="text-blue-200 text-xs mt-1">Lead Management</p>
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {sidebarCollapsed ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-start'} px-4 py-3 rounded-lg transition-all duration-200 ${
                activeTab === item.id
                  ? 'bg-blue-600 shadow-lg transform scale-105'
                  : 'hover:bg-blue-700 hover:translate-x-1'
              }`}
              title={sidebarCollapsed ? item.label : ''}
            >
              <span className="text-2xl">{item.icon}</span>
              {!sidebarCollapsed && (
                <span className="ml-3 font-medium text-sm">{item.label}</span>
              )}
            </button>
          ))}
        </nav>

        {/* User Profile Section */}
        <div className="p-4 border-t border-blue-700">
          {!sidebarCollapsed ? (
            <div className="mb-3">
              <div className="flex items-center space-x-3 mb-3">
                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">
                    {(user?.firstName || user?.teamMemberName || user?.businessName || user?.franchiseName || 'U')[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {user?.firstName || user?.teamMemberName || user?.businessName || user?.franchiseName || 'User'}
                  </p>
                  <p className="text-xs text-blue-200 truncate">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-3">
              <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">
                  {(user?.firstName || user?.teamMemberName || user?.businessName || user?.franchiseName || 'U')[0].toUpperCase()}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                title="Logout"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col overflow-hidden ${sidebarCollapsed ? 'ml-20' : 'ml-64'} transition-all duration-300`}>
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="px-6 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {menuItems.find(item => item.id === activeTab)?.label || 'Dashboard'}
                </h1>
                <p className="text-gray-600 text-sm mt-1">
                  Welcome back, {user?.firstName || user?.teamMemberName || user?.businessName || user?.franchiseName || 'User'}!
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Follow-up Notification Bell */}
                <FollowUpNotificationBell />
                
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  {user?.role || 'User'}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <div className="max-w-7xl mx-auto">
            {menuItems.find(item => item.id === activeTab)?.component}
          </div>
        </main>
      </div>

      {/* Chat System */}
      <ChatSystem
        currentUser={user}
        isOpen={showChat}
        onToggle={handleChatToggle}
      />
    </div>
  );
};

export default Dashboard;
